import {
  BALLOON_DURATION,
  BALLOON_SCORES,
  COMBO_CAP,
  DIFFICULTY,
  FRISBEE_MAX,
  MAX_SCORE_FOR_SCALING,
  POWER_DURATION_MS,
  SCORE_TIER,
  SPAWN_INTERVAL,
} from "./constants";
import type { BalloonType, DifficultyId, MatchState, PopResult, ScoreEntry } from "./types";

export function createMatchState(difficulty: DifficultyId): MatchState {
  const config = DIFFICULTY[difficulty];
  return {
    difficulty,
    lives: config.lives,
    maxLives: config.lives,
    frisbees: FRISBEE_MAX,
    score: 0,
    combo: 0,
    bestCombo: 0,
    balloonsPopped: 0,
    shots: 0,
    hits: 0,
    piercingUntil: 0,
    gameOver: false,
    gameOverReason: null,
  };
}

export function balloonScore(type: BalloonType): number {
  return BALLOON_SCORES[type];
}

export function maxBalloonsFor(difficulty: DifficultyId): number {
  return DIFFICULTY[difficulty].maxBalloons;
}

export function livesFor(difficulty: DifficultyId): number {
  return DIFFICULTY[difficulty].lives;
}

export function aimLengthFor(difficulty: DifficultyId, scale: number): number {
  const base = DIFFICULTY[difficulty].aimLength;
  const clampedScale = Math.min(1.15, Math.max(0.7, scale));
  return base * clampedScale;
}

export function difficultyTier(score: number): number {
  return Math.min(Math.floor(Math.max(0, score) / SCORE_TIER), MAX_SCORE_FOR_SCALING / SCORE_TIER);
}

export function progressionT(score: number): number {
  return Math.min(Math.max(0, score), MAX_SCORE_FOR_SCALING) / MAX_SCORE_FOR_SCALING;
}

export function balloonFlightDuration(type: BalloonType, score: number): number {
  const range = BALLOON_DURATION[type];
  const t = progressionT(score);
  return range.start + (range.end - range.start) * t;
}

export function spawnIntervalFor(score: number): number {
  const t = progressionT(score);
  return SPAWN_INTERVAL.start + (SPAWN_INTERVAL.end - SPAWN_INTERVAL.start) * t;
}

export function registerShot(state: MatchState): MatchState {
  return { ...state, shots: state.shots + 1 };
}

export function applyBalloonPop(state: MatchState, type: BalloonType, nowMs: number): PopResult {
  const combo = Math.min(state.combo + 1, COMBO_CAP);
  const points = balloonScore(type) * combo;
  const next: MatchState = {
    ...state,
    combo,
    bestCombo: Math.max(state.bestCombo, combo),
    score: state.score + points,
    frisbees: FRISBEE_MAX,
    balloonsPopped: state.balloonsPopped + 1,
    piercingUntil: type === "gold" ? nowMs + POWER_DURATION_MS : state.piercingUntil,
  };
  return { state: next, points, multiplier: combo };
}

export function markSuccessfulShot(state: MatchState): MatchState {
  return { ...state, hits: state.hits + 1 };
}

export function applyMiss(state: MatchState): MatchState {
  const frisbees = Math.max(0, state.frisbees - 1);
  const gameOver = frisbees <= 0;
  return {
    ...state,
    frisbees,
    combo: 0,
    gameOver,
    gameOverReason: gameOver ? "frisbees" : state.gameOverReason,
  };
}

export function applyBalloonGround(state: MatchState): MatchState {
  const lives = Math.max(0, state.lives - 1);
  const gameOver = lives <= 0;
  return {
    ...state,
    lives,
    combo: 0,
    gameOver,
    gameOverReason: gameOver ? "lives" : state.gameOverReason,
  };
}

export function isPiercingActive(state: MatchState, nowMs: number): boolean {
  return nowMs < state.piercingUntil;
}

export function powerRemainingMs(state: MatchState, nowMs: number): number {
  return Math.max(0, state.piercingUntil - nowMs);
}

export function accuracyPercent(state: MatchState): number {
  if (state.shots <= 0) return 0;
  return Math.round((state.hits / state.shots) * 100);
}

export function insertScore(list: ScoreEntry[], entry: ScoreEntry, limit = 5): ScoreEntry[] {
  return [...list, entry]
    .sort((a, b) => b.score - a.score || b.date.localeCompare(a.date))
    .slice(0, limit);
}

export function isNewRecord(list: ScoreEntry[], score: number): boolean {
  if (list.length < 5) return score > 0;
  const last = list[list.length - 1];
  return Boolean(last && score > last.score);
}
